import React from 'react';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="w-full max-w-4xl bg-white p-8 rounded-lg shadow-lg">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">Privacy Policy</h1>
      <p className="text-sm text-gray-500 mb-8">Effective Date: September 27, 2026</p>

      <div className="space-y-6 text-gray-700">
        <p>
          Cesdiacomm, LLC, doing business as Typing Masters ("Typing Masters", "we", "our", or "us"), is committed to protecting the privacy of our users’ personal information. This Privacy Policy applies to the Typing Masters website (TypeoGO.com), any affiliated subdomains, and governs our data collection, processing, and usage practices. Access to and use of Typing Masters and the services we provide ("Services") is subject to this Privacy Policy.
        </p>

        <div>
          <h2 className="text-xl font-semibold mb-2">Scope and Audience</h2>
          <p>
            Typing Masters is designed for educational use by schools and educational institutions. Student and child accounts can only be created and managed by authorized teachers or school officials—individual student self-registration is not permitted. A publicly accessible Demo section is available for preview purposes and general information, requiring no registration.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold mb-2">Children’s Privacy</h2>
          <p>
            Typing Masters does not knowingly permit the creation of student or child accounts except by authorized school officials or teachers. Schools and teachers are responsible for obtaining all necessary parental consents under the Children’s Online Privacy Protection Act (COPPA) and the Family Educational Rights and Privacy Act (FERPA) or similar local laws before registering children under 13 in the United States (or under 16 in the European Union or other regions with stricter regulations).
          </p>
          <p className="mt-2">
            Parents and legal guardians may contact privacy@TypeoGO.com to review, update, or request deletion of their child’s data at any time.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold mb-2">Information We Collect</h2>
          <p>We may collect information directly from users (teachers/school officials and, via their input, students), and through limited automated means (cookies or other tracking technologies).</p>
          <p className="mt-2">When a teacher or school registers for an account and creates student accounts, the information collected may include:</p>
          <ul className="list-disc list-inside space-y-1 mt-2 pl-4">
            <li><strong>Teacher/School Official:</strong> Name, email address, school affiliation, title/role, and contact details.</li>
            <li><strong>Student:</strong> We deliberately do <strong>not</strong> collect a student's real name or email address. Each student is identified only by a short, anonymous username assigned by their teacher (a class prefix plus a number, e.g. "cls51"), a password, and their class/school affiliation.</li>
            <li><strong>Performance data:</strong> Progress and results from using Typing Masters (lesson completions, typing speed and accuracy, keys practiced, game scores).</li>
          </ul>
          <p className="mt-2">For all users:</p>
          <ul className="list-disc list-inside space-y-1 mt-2 pl-4">
            <li><strong>Technical information</strong> such as IP address, browser type, device type, operating system, and usage data may be collected through cookies and server logs for improving security, analytics, and user experience.</li>
            <li><strong>Demo section:</strong> No personally identifiable information is collected unless a user chooses to contact us or register for further information.</li>
          </ul>
        </div>
        
        <div>
          <h2 className="text-xl font-semibold mb-2">Cookies and Tracking Technologies</h2>
          <p>When you first visit our site, you're asked to choose one of three cookie categories:</p>
          <ul className="list-disc list-inside space-y-1 mt-2 pl-4">
            <li><strong>Strictly Necessary</strong> -- required for login, security, and core site functionality. These can't be disabled.</li>
            <li><strong>Performance &amp; Analytics</strong> -- help us understand aggregated, anonymous site traffic so we can improve the Service.</li>
            <li><strong>Functional</strong> -- remember preferences such as your accessibility settings.</li>
          </ul>
          <p className="mt-2">You can choose "Necessary Only" to opt out of everything but the essentials, or open <strong>Cookie Settings</strong> (linked in the footer of every page) at any time to change your choice.</p>
        </div>

        <div>
          <h2 className="text-xl font-semibold mb-2">How We Use Information</h2>
          <p>We use personal information to:</p>
          <ul className="list-disc list-inside space-y-1 mt-2 pl-4">
            <li>Provide and operate the Services.</li>
            <li>Communicate with school officials, teachers, and (where appropriate) parents/guardians.</li>
            <li>Manage and improve the security and functionality of Typing Masters.</li>
            <li>Comply with legal obligations and school/district policies.</li>
          </ul>
        </div>
        
        <div>
          <h2 className="text-xl font-semibold mb-2">Information Sharing and Disclosure</h2>
          <ul className="list-disc list-inside space-y-1 mt-2">
            <li>Student information is only accessible to authorized teachers and school officials, except as required by law.</li>
            <li>We do not sell or share student information with third parties for advertising or marketing purposes.</li>
            <li>Aggregated, de-identified data may be used for internal analysis and to improve our Services.</li>
            <li>We may use third-party service providers (such as secure hosting providers or analytics tools). These providers are contractually required to protect all personal information and may not use it for any other purpose.</li>
            <li>If required by law, in case of a merger, acquisition, or divestiture, or to protect the security and integrity of the Service, we may disclose information only as necessary and with appropriate safeguards.</li>
          </ul>
        </div>

        <div>
          <h2 className="text-xl font-semibold mb-2">Student and Children’s Data Rights</h2>
          <ul className="list-disc list-inside space-y-1 mt-2">
            <li>Parents or legal guardians may request to review, update, or delete their child’s personal information at any time by emailing privacy@TypeoGO.com.</li>
            <li>Schools and teachers must ensure parental consent is obtained where required before collecting or providing any student information.</li>
          </ul>
        </div>
        
        <div>
          <h2 className="text-xl font-semibold mb-2">Data Security</h2>
          <p>We use administrative, technical, and physical measures to safeguard personal information against loss, theft, and unauthorized use or disclosure.</p>
        </div>

        <div>
          <h2 className="text-xl font-semibold mb-2">Do Not Track</h2>
          <p>Typing Masters does not track users across third-party websites and does not respond to “Do Not Track” signals.</p>
        </div>

        <div>
          <h2 className="text-xl font-semibold mb-2">International Users</h2>
          <p>Typing Masters is operated from the United States. If you use our services from outside the U.S., you consent to the transfer of your information to the U.S., where data protection laws may differ from those of your country.</p>
        </div>

        <div>
          <h2 className="text-xl font-semibold mb-2">Changes to This Privacy Policy</h2>
          <p>We may update this Privacy Policy from time to time. If we make material changes, we will notify registered schools and post the updated policy on our website. Continued use of the Services constitutes acceptance of the updated policy.</p>
        </div>

        <div>
          <h2 className="text-xl font-semibold mb-2">Contact Us</h2>
          <p>For questions regarding this Privacy Policy or your data rights, contact us at privacy@typeogo.com.</p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;